import React from 'react';
import { Grid, Chip, Typography, Stack } from '@mui/material';
import {
  DashboardHeader, StatTile, KpiRow, Section, BarList, AttentionList, RowList, StatusChip, fmtDate, compact, LinkButton,
} from './DashboardParts';

export default function AdminDashboard({ data }) {
  const k = data.kpis;
  const a = data.attention;

  // Everything waiting on the admin, as one actionable list
  const attention = [
    ...a.vendorApprovals.map(c => ({
      text: `Approve vendor contract ${c.ref}: ${c.title.replace(/^Vendor Services Agreement:\s*/, '')}`,
      secondary: `${c.services || 'Services'} · vendor has signed`, link: '/dashboard/admin/contracts',
    })),
    ...a.familyContractsToSign.map(c => ({
      text: `Countersign family contract ${c.ref}: ${c.title}`, secondary: c.email, link: '/dashboard/admin/contracts',
    })),
    ...a.changeRequests.map(c => ({
      text: `Changes requested on ${c.ref}: ${c.title}`, secondary: c.email, link: '/dashboard/admin/contracts',
    })),
    ...a.wishlistApprovals.map(w => ({
      text: `Approve service wishlist from ${w.name}`, secondary: `${w.services} service categories · ${fmtDate(w.created_at)}`, link: '/dashboard/admin/wishlist-approvals',
    })),
    ...a.vendorAwaitingSignature.map(c => ({
      text: `Waiting for vendor to sign ${c.ref}`, secondary: c.email, link: '/dashboard/admin/contracts', waiting: true,
    })),
  ];
  const actionable = attention.filter(i => !i.waiting).length;

  return (
    <>
      <DashboardHeader
        title="Admin dashboard"
        subtitle="Platform activity and everything waiting on you"
        chip={<Chip color={actionable ? 'warning' : 'success'} label={actionable ? `${actionable} item(s) need action` : 'Nothing waiting'} />}
      />
      <KpiRow>
        <StatTile hero label="Items needing action" value={actionable} sub="approvals, signatures and change requests" />
        <StatTile label="Families" value={compact(k.families)} sub={`${k.brides} bride · ${k.grooms} groom`} to="/dashboard/admin/users" />
        <StatTile label="Vendors live" value={`${k.liveVendors} / ${k.vendors}`} sub="with a signed service contract" to="/dashboard/admin/contracts" />
        <StatTile label="Offerings live" value={`${k.liveOfferings} / ${k.offerings}`} sub={`${k.packages} packages listed`} to="/dashboard/service-providers" />
      </KpiRow>
      <KpiRow>
        <StatTile label="Total users" value={compact(k.totalUsers)} sub={`${k.newUsersThisWeek} new this week`} to="/dashboard/admin/users" />
        <StatTile label="Matchmakers" value={k.matchmakers} to="/dashboard/admin/users" />
        <StatTile label="Guests registered" value={compact(k.guests)} sub="across all families" />
        <StatTile label="Wishlists to approve" value={a.wishlistApprovals.length} to="/dashboard/admin/wishlist-approvals" />
      </KpiRow>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={7}>
          <Section title="Needs your attention">
            <AttentionList items={attention} emptyText="No approvals or signatures waiting." />
          </Section>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Section title="Users by role">
            <BarList data={data.usersByRole.map(r => ({ label: r.label, value: r.count }))} />
          </Section>
        </Grid>
        <Grid item xs={12} lg={7}>
          <Section title="Vendor offerings by service" action={<LinkButton to="/dashboard/service-providers">View all</LinkButton>}>
            <BarList
              data={data.offeringsByCategory.map(c => ({ label: c.label, value: c.live, rest: c.hidden }))}
              valueLabel="Live" restLabel="Hidden"
              emptyText="No vendor offerings yet."
            />
          </Section>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Section title="Contracts by status">
            <RowList
              rows={data.contractsByStatus.map(c => ({
                primary: <StatusChip status={c.status} />,
                right: <Typography variant="body2" fontWeight={600}>{c.count}</Typography>,
              }))}
              empty="No contracts yet."
            />
          </Section>
        </Grid>
        <Grid item xs={12}>
          <Section title="Newest users" action={<LinkButton to="/dashboard/admin/users">Manage users</LinkButton>}>
            <RowList
              rows={data.recentUsers.map(u => ({
                primary: u.email, secondary: u.role,
                right: <Stack><Typography variant="caption" color="text.secondary">{fmtDate(u.created_at)}</Typography></Stack>,
              }))}
            />
          </Section>
        </Grid>
      </Grid>
    </>
  );
}
